import React from 'react';
import I18n from 'i18n-js';
import moment from 'moment';
import { useInView } from 'react-intersection-observer';
import {
  BellIcon,
  ChartLineIcon,
  ClockIcon,
  HeartStraightIcon,
  MagnifyingGlassIcon,
  MapPinIcon,
  PackageIcon,
  PencilSimpleIcon,
  ShoppingCartIcon,
  StarIcon,
  TagIcon,
} from '@phosphor-icons/react';
import { RCIcon, RCText } from '@reverbdotcom/cadence/components';
import { ReverbLogoCircleIcon } from '@reverbdotcom/cadence/icons/react';
import CoreLink from '@reverbdotcom/commons/src/components/core_link';
import { NotificationsCenterQuery } from '@reverbdotcom/commons/src/gql/graphql';
import { clickedNotificationEvent } from './notification_event_props';

type NotificationStreamItem = NotificationsCenterQuery['notificationStream']['items'][number];

type StreamGroup = 'new' | 'earlier';

const STREAM_ICONS: Record<string, JSX.Element> = {
  tag: <TagIcon />,
  'shopping-cart': <ShoppingCartIcon />,
  heart: <HeartStraightIcon />,
  clock: <ClockIcon />,
  package: <PackageIcon />,
  'chart-line': <ChartLineIcon />,
  'map-pin': <MapPinIcon />,
  'magnifying-glass': <MagnifyingGlassIcon />,
  'pencil-simple': <PencilSimpleIcon />,
  star: <StarIcon />,
  'reverb-logo-circle': <ReverbLogoCircleIcon />,
};

function streamIcon(iconName?: string | null): JSX.Element {
  if (!iconName) { return <BellIcon />; }

  return STREAM_ICONS[iconName] ?? <BellIcon />;
}

/**
 * Which indices carry a group heading, keyed by index. Each heading is emitted at most once,
 * at the first item of its read state, so items stay in the order the API returned them: the
 * server sorts the page and drives the `position` in event tracking, so re-partitioning here
 * would desync the two. An unread item arriving after the first read one renders under
 * EARLIER rather than starting a second NEW group.
 *
 * Built once for the whole list rather than asked per row - infinite scroll keeps appending
 * pages, and a per-row scan over the list would grow quadratically with it.
 */
export function groupHeadings(items: NotificationStreamItem[]): Map<number, StreamGroup> {
  const firstUnread = items.findIndex(item => !item.isRead);
  const firstRead = items.findIndex(item => item.isRead);
  const headings = new Map<number, StreamGroup>();

  if (firstUnread !== -1) { headings.set(firstUnread, 'new'); }
  if (firstRead !== -1) { headings.set(firstRead, 'earlier'); }

  return headings;
}

interface IProps {
  items: NotificationStreamItem[];
  hasMore: boolean;
  onLoadMore: () => void;
}

export default function StreamSection({ items, hasMore, onLoadMore }: IProps) {
  if (items.length === 0) return null;

  const headings = groupHeadings(items);

  return (
    <div className="notifications-center__stream">
      {items.map((item, index) => {
        const heading = headings.get(index);

        return (
          <React.Fragment key={item.id}>
            {heading && <StreamGroupHeading group={heading} />}
            <StreamItem item={item} />
          </React.Fragment>
        );
      })}
      {hasMore && <StreamScrollSentinel onEnterView={onLoadMore} />}
    </div>
  );
}

export function StreamGroupHeading({ group }: { group: StreamGroup }) {
  return (
    <div className="notifications-center__stream__heading">
      <RCText.Eyebrow size="300" color="tertiary" htmlTag="span">
        {group === 'new'
          ? I18n.t('discovery.notificationsCenter.stream.new')
          : I18n.t('discovery.notificationsCenter.stream.earlier')}
      </RCText.Eyebrow>
      {/* The NEW heading holds the slot AR-518 drops Mark all as read into. Reserved
          rather than rendered so the heading's layout doesn't move when it arrives. */}
      {group === 'new' && <span className="notifications-center__stream__heading__action" />}
    </div>
  );
}

export function StreamItem({ item }: { item: NotificationStreamItem }) {
  return (
    <CoreLink
      to={item.href}
      className="notifications-center__stream__item"
      clickEvent={clickedNotificationEvent(item.eventTracking)}
    >
      <StreamItemMedia item={item} />
      <span className="notifications-center__stream__item__text">
        {item.eyebrow && (
          <RCText.Eyebrow size="300" color="urgent" htmlTag="span">
            {item.eyebrow}
          </RCText.Eyebrow>
        )}
        <RCText.Utility size="350" weight="semibold" htmlTag="span">
          {item.title}
        </RCText.Utility>
        {item.body && (
          <RCText.Default size="350" htmlTag="span">
            {item.body}
          </RCText.Default>
        )}
        <RCText.Fine size="300" color="secondary" htmlTag="span">
          {moment(item.createdAt).fromNow()}
        </RCText.Fine>
      </span>
      {!item.isRead && (
        <span className="notifications-center__stream__item__unread">
          <span className="visually-hidden">
            {I18n.t('discovery.notificationsCenter.stream.unread')}
          </span>
        </span>
      )}
    </CoreLink>
  );
}

/**
 * Every kind resolves an icon, including the photo-led ones, so an item whose entity has
 * no primary image still shows something sensible rather than an empty frame.
 */
function StreamItemMedia({ item }: { item: NotificationStreamItem }) {
  if (item.thumbnailUrl) {
    return (
      <span className="notifications-center__stream__item__thumbnail">
        <img src={item.thumbnailUrl} alt="" />
      </span>
    );
  }

  return (
    <span className="notifications-center__stream__item__icon">
      <RCIcon icon={streamIcon(item.iconName)} size={7} />
    </span>
  );
}

/**
 * Infinite scroll, no Load More affordance. Watched rather than measured, so the effect
 * re-runs whenever `onEnterView` changes identity - which it does after each page lands.
 * A page short enough to leave the sentinel on screen therefore keeps filling instead of
 * stalling on an intersection that never transitions again.
 */
export function StreamScrollSentinel({ onEnterView }: { onEnterView: () => void }) {
  const { ref, inView } = useInView();

  React.useEffect(() => {
    if (inView) { onEnterView(); }
  }, [inView, onEnterView]);

  return (
    <div
      ref={ref}
      className="notifications-center__stream__sentinel"
      data-testid="notifications-center-stream-sentinel"
      aria-hidden
    />
  );
}
